import React, { useMemo } from 'react';
import { CalendarEvent } from '../../hooks/useEvents';
import { styled } from '../../styles/stitches.config';
import { useUser } from '../../contextManager/user.context';

const StatsGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '0.75rem',
  marginBottom: '1.5rem',
});

const StatCard = styled('div', {
  background: 'white',
  borderRadius: 12,
  padding: '1rem',
  border: '1px solid $sand5',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
});

const StatValue = styled('div', {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '$blue11',
  lineHeight: 1,
});

const StatLabel = styled('div', {
  fontSize: '0.75rem',
  color: '$sand10',
  fontWeight: 500,
});

const SectionTitle = styled('h3', {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '$sand12',
  margin: '0 0 0.75rem',
});

const BarRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '0.4rem',
});

const BarLabel = styled('span', {
  fontSize: '0.75rem',
  color: '$sand11',
  width: 90,
  flexShrink: 0,
  textAlign: 'right',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const BarTrack = styled('div', {
  flex: 1,
  height: 18,
  background: '$sand3',
  borderRadius: 4,
  overflow: 'hidden',
  position: 'relative',
});

const BarFill = styled('div', {
  height: '100%',
  borderRadius: 4,
  transition: 'width 0.4s ease',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '0.35rem',
  fontSize: '0.65rem',
  fontWeight: 600,
  color: 'white',
});

const EmptyState = styled('div', {
  textAlign: 'center',
  padding: '3rem 1rem',
  color: '$sand10',
  fontSize: '0.9rem',
});

const WeekGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gap: '3px',
});

const WeekCell = styled('div', {
  height: 18,
  borderRadius: 3,
  transition: 'background 0.2s',
});

interface LessonStatsProps {
  events: CalendarEvent[];
}

export const LessonStats: React.FC<LessonStatsProps> = ({ events }) => {
  const { userDetails } = useUser();
  const isTutor = userDetails?.role === 'tutor';

  const stats = useMemo(() => {
    const now = new Date();
    const completed = events.filter(e =>
      e.eventType === 'lesson' && new Date(e.endAt) < now
    );

    // Total hours
    const totalMs = completed.reduce((sum, e) =>
      sum + (new Date(e.endAt).getTime() - new Date(e.startAt).getTime()), 0
    );
    const totalHours = Math.round(totalMs / 3600000 * 10) / 10;

    // Subjects breakdown (extract from title like "A-Level Maths lesson")
    const subjectCounts = new Map<string, number>();
    for (const e of completed) {
      // Try to extract subject from title
      const match = e.title.match(/(?:Lesson \d+: )?(.+?)(?:\s+lesson)?$/i);
      const subject = match?.[1]?.trim() || e.title;
      subjectCounts.set(subject, (subjectCounts.get(subject) || 0) + 1);
    }
    const subjects = Array.from(subjectCounts.entries())
      .sort((a, b) => b[1] - a[1]);

    // Unique counterparties
    const counterparties = new Set(
      completed.map(e => isTutor ? e.parentId : e.tutorId).filter(Boolean)
    );

    // Weekly activity (last 12 weeks)
    const weeklyData: number[] = [];
    for (let i = 11; i >= 0; i--) {
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - (i * 7) - weekStart.getDay() + 1);
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);

      const count = completed.filter(e => {
        const d = new Date(e.startAt);
        return d >= weekStart && d < weekEnd;
      }).length;
      weeklyData.push(count);
    }
    const maxWeekly = Math.max(...weeklyData, 1);

    // Current streak (consecutive weeks with at least 1 lesson, counting back from this week)
    let streak = 0;
    for (let i = weeklyData.length - 1; i >= 0; i--) {
      if (weeklyData[i] > 0) streak++;
      else break;
    }

    return {
      totalLessons: completed.length,
      totalHours,
      counterpartyCount: counterparties.size,
      subjects,
      weeklyData,
      maxWeekly,
      streak,
    };
  }, [events, isTutor]);

  if (stats.totalLessons === 0) {
    return (
      <EmptyState>
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="12" y="24" width="8" height="32" rx="3" fill="var(--colors-blue3, #dbeafe)" stroke="var(--colors-blue7, #93c5fd)" strokeWidth="1.5"/>
          <rect x="26" y="34" width="8" height="22" rx="3" fill="var(--colors-blue4, #bfdbfe)" stroke="var(--colors-blue7, #93c5fd)" strokeWidth="1.5"/>
          <rect x="40" y="18" width="8" height="38" rx="3" fill="var(--colors-blue3, #dbeafe)" stroke="var(--colors-blue7, #93c5fd)" strokeWidth="1.5"/>
          <rect x="54" y="28" width="8" height="28" rx="3" fill="var(--colors-blue4, #bfdbfe)" stroke="var(--colors-blue7, #93c5fd)" strokeWidth="1.5"/>
          <line x1="8" y1="58" x2="72" y2="58" stroke="var(--colors-sand6)" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="66" cy="14" r="5" fill="var(--colors-blue9, #3b82f6)" opacity="0.2"/>
          <path d="M64 14h4M66 12v4" stroke="var(--colors-blue9)" strokeWidth="1" strokeLinecap="round"/>
        </svg>
        <p style={{ margin: '0.5rem 0 0.25rem', fontWeight: 600, fontSize: '0.95rem', color: 'var(--colors-sand12)' }}>
          No stats yet
        </p>
        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--colors-sand10)', lineHeight: 1.4 }}>
          Complete your first lesson and your learning stats will appear here.
        </p>
      </EmptyState>
    );
  }

  const subjectColors = ['$blue9', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];

  return (
    <div style={{ padding: '0.5rem 0' }}>
      {/* Top-line stats */}
      <StatsGrid>
        <StatCard>
          <StatValue>{stats.totalLessons}</StatValue>
          <StatLabel>Lessons completed</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.totalHours}</StatValue>
          <StatLabel>Hours {isTutor ? 'taught' : 'learned'}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.counterpartyCount}</StatValue>
          <StatLabel>{isTutor ? 'Students' : 'Tutors'}</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.streak}</StatValue>
          <StatLabel>Week streak</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* Weekly activity heatmap */}
      <SectionTitle>Weekly Activity (last 12 weeks)</SectionTitle>
      <WeekGrid>
        {stats.weeklyData.map((count, i) => {
          const intensity = count / stats.maxWeekly;
          const bg = count === 0
            ? 'var(--colors-sand3)'
            : `rgba(59, 130, 246, ${0.2 + intensity * 0.8})`;
          return (
            <WeekCell
              key={i}
              style={{ background: bg }}
              title={`${count} lesson${count !== 1 ? 's' : ''}`}
            />
          );
        })}
      </WeekGrid>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: 'var(--colors-sand9)', marginTop: '0.25rem' }}>
        <span>12 weeks ago</span>
        <span>This week</span>
      </div>

      {/* Subject breakdown */}
      {stats.subjects.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <SectionTitle>Subjects</SectionTitle>
          {stats.subjects.slice(0, 6).map(([subject, count], i) => {
            const pct = (count / stats.totalLessons) * 100;
            const color = subjectColors[i % subjectColors.length];
            return (
              <BarRow key={subject}>
                <BarLabel title={subject}>{subject}</BarLabel>
                <BarTrack>
                  <BarFill style={{
                    width: `${Math.max(pct, 8)}%`,
                    background: typeof color === 'string' && color.startsWith('$')
                      ? `var(--colors-${color.slice(1)})`
                      : color,
                  }}>
                    {count}
                  </BarFill>
                </BarTrack>
              </BarRow>
            );
          })}
        </div>
      )}
    </div>
  );
};
